import React from 'react';
import { SliderField } from '@/components/inspector-fields/SliderField/SliderField';
import { ColorField } from '@/components/inspector-fields/ColorField/ColorField';
import type { RGBA } from '@builder4/shared/types/layout';
import { IconSimpleButton } from '@/components/buttons/IconSimpleButton/IconSimpleButton';
import IconClose from '@/assets/icons/icon-close.svg?react';
import clsx from 'clsx';
import { hexToRgba } from '@builder4/shared/utils/styleHelpers';

export interface DropShadowSettings {
  x: number; // X Offset
  y: number; // Y Offset
  blur: number;
  spread: number;
  color: string; // Hex string for simplicity
}

interface DropShadowMenuProps {
  settings: DropShadowSettings;
  onChange: (next: DropShadowSettings) => void;
  onClose: () => void;
  className?: string;
  floatingPosition?: { top: number; left: number };
  outside?: boolean; // renders as portal-like floating panel
}

const DropShadowMenu = React.forwardRef<HTMLDivElement, DropShadowMenuProps>(
  ({ settings, onChange, onClose, className = '', floatingPosition, outside = false }, ref) => {
    const update = (partial: Partial<DropShadowSettings>) => {
      onChange({ ...settings, ...partial });
    };

    return (
      <div
        className={clsx(
          'bg-neutral-0 z-50 w-[240px] rounded-lg shadow-[0px_12px_24px_0px_rgba(0,0,0,0.12)] outline outline-neutral-100',
          outside ? 'fixed' : 'absolute',
          outside ? '' : 'top-0 right-0',
          className,
        )}
        style={
          outside && floatingPosition
            ? { top: floatingPosition.top, left: floatingPosition.left }
            : undefined
        }
        ref={ref}
      >
        <div className="inline-flex h-10 w-full items-center justify-between rounded-t-lg border border-neutral-100 px-3">
          <div className="text-xs font-semibold text-neutral-900">Dropshadow</div>
          <IconSimpleButton aria-label="Close" icon={IconClose} onClick={onClose} />
        </div>
        <div className="flex flex-col gap-3 border border-t-0 border-neutral-100 px-3 pt-3 pb-4">
          <SliderField
            label="X Offset"
            value={settings.x}
            onChange={(v) => update({ x: v })}
            min={-100}
            max={100}
          />
          <SliderField
            label="Y Offset"
            value={settings.y}
            onChange={(v) => update({ y: v })}
            min={-100}
            max={100}
          />
          <SliderField
            label="Blur"
            value={settings.blur}
            onChange={(v) => update({ blur: v })}
            min={0}
            max={200}
          />
          <SliderField
            label="Spread"
            value={settings.spread}
            onChange={(v) => update({ spread: v })}
            min={0}
            max={200}
          />
          <ColorField
            className="w-full"
            text="Color"
            mode="solid"
            hasGradient={false}
            inputValue={settings.color.replace('#', '')}
            color={hexToRgba(settings.color)}
            onChange={(value, mode) => {
              if (mode !== 'solid' || !value || !('r' in value)) return;
              const { r, g, b, a } = value as RGBA;
              const toHex = (n: number) => n.toString(16).padStart(2, '0');
              const alphaHex = a < 1 ? toHex(Math.round(a * 255)) : '';
              update({ color: `#${toHex(r)}${toHex(g)}${toHex(b)}${alphaHex}`.toUpperCase() });
            }}
            onChangeComplete={(value) => {
              if (!value || typeof value === 'string' || !('r' in value)) return;
              const { r, g, b, a } = value as RGBA;
              const toHex = (n: number) => n.toString(16).padStart(2, '0');
              const alphaHex = a < 1 ? toHex(Math.round(a * 255)) : '';
              update({
                color: `#${toHex(r)}${toHex(g)}${toHex(b)}${alphaHex}`.toUpperCase(),
              });
            }}
          />
        </div>
      </div>
    );
  },
);

DropShadowMenu.displayName = 'DropShadowMenu';

export default DropShadowMenu;
